import FormikInput from "components/input/formik-input";
import { registrationEnum } from "enums/glimps.enums";
import { ErrorMessage, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import { IHelpSomeoneLearn } from "interfaces/RegisterInterface";
import React from "react";
import InputError from "components/input-error/input-error";
import { displayWarning } from "components/Toast/toast.component";
import Button from "components/button/button";

const HelpSomeoneLearn: React.FC<IHelpSomeoneLearn> = ({
  setActiveRegStep,
  setActiveTab,
  handleGiftInvitation,
  email,
  purchaserInfo,
}) => {
  const validationSchema = Yup.object().shape({
    emailList: Yup.array().of(
      Yup.string().email("Invalid email format").required("Required")
    ),
  });

  return (
    <>
      <div className="flex flex-col justify-center topic">
        <h2 className="text-display-lg text-white text-center">
          Help Someone Learn
        </h2>
        <p className="px-10 mt-4 text-base font-normal leading-4 text-white text-center min-w-[327px]">
          Provide the emails for every person you want to send a code to.
        </p>
        {purchaserInfo?.purchaser_email && (
          <p className="px-10 mt-2 text-sm font-normal text-white text-center opacity-80">
            Receipt will be sent to {purchaserInfo.purchaser_email}
          </p>
        )}
      </div>
      <div className="flex justify-center py-8">
        <div className="bg-white rounded-lg shadow-card w-full max-w-[480px] p-8 flex flex-col">
          <Formik
            initialValues={email}
            validationSchema={validationSchema}
            onSubmit={handleGiftInvitation}
          >
            {({ isSubmitting, values, errors }) => (
              <Form className="flex flex-col gap-4">
                {errors?.emailList &&
                  typeof errors.emailList === "string" && (
                    <ErrorMessage name={"emailList"} component={InputError} />
                  )}
                <FieldArray
                  name="emailList"
                  render={({ push, pop }) => (
                    <>
                      <div className="flex items-center justify-center gap-4">
                        <button
                          type="button"
                          aria-label="Remove recipient"
                          className="cursor-pointer shrink-0 bg-transparent border-none p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={values.emailList.length <= 1}
                          onClick={() => {
                            if (values.emailList.length > 1) {
                              pop();
                            } else {
                              displayWarning("You cannot remove the last item.");
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="28"
                            height="28"
                            viewBox="0 0 26 26"
                            fill="none"
                          >
                            <circle cx="13" cy="13" r="13" fill="#213269" />
                            <path d="M6 13H20" stroke="white" strokeWidth="3" strokeLinecap="round" />
                          </svg>
                        </button>

                        <div className="relative">
                          <img
                            src="/assets/icons/gift_pack.svg"
                            alt="Gift"
                            className="w-16 h-16"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="flex items-center justify-center w-8 h-8 bg-white rounded-full shadow-subtle">
                              <span className="text-body-emphasis text-brand">
                                {values.emailList.length}
                              </span>
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          aria-label="Add recipient"
                          className="cursor-pointer shrink-0 bg-transparent border-none p-0"
                          onClick={() => push("")}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="28"
                            height="28"
                            viewBox="0 0 26 26"
                            fill="none"
                          >
                            <circle cx="13" cy="13" r="13" fill="#213269" />
                            <path d="M6 13H20" stroke="white" strokeWidth="3" strokeLinecap="round" />
                            <path d="M13 6L13 20" stroke="white" strokeWidth="3" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>

                      <div className="flex flex-col gap-3">
                        {values.emailList.map((item, index: number) => (
                          <div key={index}>
                            <FormikInput
                              id={`emailList.${index}`}
                              name={`emailList.${index}`}
                              label={index === 0 && "Email"}
                              showLabel={index === 0}
                              type="text"
                              as="input"
                              placeholder="Email"
                              maxLength={50}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                />

                <div className="flex justify-between gap-4 pt-2">
                  <Button
                    type="button"
                    onClick={() => setActiveRegStep(registrationEnum.purchaser)}
                    variant="outline"
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="flex-1"
                  >
                    Continue to Cart
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <div className="flex justify-center">
        <p className="text-sm font-normal text-white">
          <span>Want to redeem a code you received?</span>
          <span
            className="pl-1 font-bold underline cursor-pointer text-white underline-offset-2"
            onClick={() => setActiveTab(2)}
          >
            Redeem Code
          </span>
        </p>
      </div>
    </>
  );
};

export default HelpSomeoneLearn;
